/**
 * Asset Hierarchy Levels Service
 * 
 * @swagger-tag AssetHierarchyLevels
 * @description Controller for asset hierarchy level operations.
 */
import ApiService from './api';
import type { components } from '@/core/types/generated/api';
import { buildPageQuery, toPaginatedResult, type PaginatedResult, type PageRequest } from '@/shared/utils/pagination';

// Type aliases for better readability
export type AssetHierarchyLevelDto = components['schemas']['AssetHierarchyLevelDto'];
export type InsertAssetHierarchyLevelCommand = components['schemas']['InsertAssetHierarchyLevelCommand'];
export type UpdateAssetHierarchyLevelCommand = components['schemas']['UpdateAssetHierarchyLevelCommand'];
type AssetHierarchyLevelDtoPaginatedResponse = components['schemas']['AssetHierarchyLevelDtoPaginatedResponse'];
type BulkCreateAssetHierarchyLevelsRequest = components['schemas']['BulkCreateAssetHierarchyLevelsRequest'];
type BulkUpdateAssetHierarchyLevelsRequest = components['schemas']['BulkUpdateAssetHierarchyLevelsRequest'];
type BulkDeleteAssetHierarchyLevelsRequest = components['schemas']['BulkDeleteAssetHierarchyLevelsRequest'];
type AssetHierarchyLevelDtoBulkOperationResult = components['schemas']['AssetHierarchyLevelDtoBulkOperationResult'];
type GuidBulkOperationResult = components['schemas']['GuidBulkOperationResult'];

// Query parameters for paginated asset hierarchy levels
export interface AssetHierarchyLevelsListParams extends PageRequest {
  name?: string;
  useForSurroundSearch?: boolean;
}

export const AssetHierarchyLevelsService = {
  /**
   * Gets all asset hierarchy levels without pagination.
   * GET /api/AssetHierarchyLevels/all
   */
  async getAll(): Promise<AssetHierarchyLevelDto[]> {
    const response = await ApiService.get<AssetHierarchyLevelDto[]>('/AssetHierarchyLevels/all');
    return response.data;
  },

  /**
   * Gets paginated, filtered, and sorted asset hierarchy levels.
   * GET /api/AssetHierarchyLevels
   */
  async getPaged(params: AssetHierarchyLevelsListParams = {}): Promise<PaginatedResult<AssetHierarchyLevelDto, AssetHierarchyLevelDtoPaginatedResponse>> {
    const query = buildPageQuery(params);
    const response = await ApiService.get<AssetHierarchyLevelDtoPaginatedResponse>('/AssetHierarchyLevels', { params: query });
    return toPaginatedResult<AssetHierarchyLevelDto, AssetHierarchyLevelDto, AssetHierarchyLevelDtoPaginatedResponse>(
      response.data, 
      (item) => item as AssetHierarchyLevelDto
    );
  },

  /**
   * Gets a specific asset hierarchy level by ID.
   * Since there's no direct getById endpoint, this uses getAll and filters.
   * @param levelId - The ID of the level to retrieve
   */
  async getById(levelId: string): Promise<AssetHierarchyLevelDto | null> {
    const allLevels = await this.getAll();
    return allLevels.find(level => level.id === levelId) || null;
  },

  /**
   * Creates a new asset hierarchy level.
   * API expects InsertAssetHierarchyLevelCommand on POST /api/AssetHierarchyLevels
   */
  async create(command: InsertAssetHierarchyLevelCommand): Promise<AssetHierarchyLevelDto> {
    const response = await ApiService.post<AssetHierarchyLevelDto>('/AssetHierarchyLevels', command);
    return response.data;
  },

  /**
   * Updates an existing asset hierarchy level.
   * PUT /api/AssetHierarchyLevels/{levelId}
   */
  async update(levelId: string, command: UpdateAssetHierarchyLevelCommand): Promise<AssetHierarchyLevelDto> {
    const response = await ApiService.put<AssetHierarchyLevelDto>(`/AssetHierarchyLevels/${levelId}`, {
      ...command,
      levelId,
    });
    return response.data;
  },

  /**
   * Inserts a new asset hierarchy level after a specific level.
   * POST /api/AssetHierarchyLevels/insert
   */
  async insert(command: InsertAssetHierarchyLevelCommand): Promise<AssetHierarchyLevelDto> {
    const response = await ApiService.post<AssetHierarchyLevelDto>('/AssetHierarchyLevels/insert', command);
    return response.data;
  },

  /**
   * Deletes an asset hierarchy level.
   * DELETE /api/AssetHierarchyLevels/{levelId}
   */
  async delete(levelId: string): Promise<void> {
    await ApiService.delete(`/AssetHierarchyLevels/${levelId}`);
  },

  /**
   * Bulk updates multiple asset hierarchy levels.
   * PUT /api/AssetHierarchyLevels/bulk
   */
  async bulkUpdate(request: BulkUpdateAssetHierarchyLevelsRequest): Promise<AssetHierarchyLevelDtoBulkOperationResult> {
    const response = await ApiService.put<AssetHierarchyLevelDtoBulkOperationResult>('/AssetHierarchyLevels/bulk', request);
    return response.data;
  },

  /**
   * Bulk creates multiple asset hierarchy levels.
   * POST /api/AssetHierarchyLevels/bulk
   */
  async bulkCreate(request: BulkCreateAssetHierarchyLevelsRequest): Promise<AssetHierarchyLevelDtoBulkOperationResult> {
    const response = await ApiService.post<AssetHierarchyLevelDtoBulkOperationResult>('/AssetHierarchyLevels/bulk', request);
    return response.data;
  },

  /**
   * Bulk deletes multiple asset hierarchy levels.
   * DELETE /api/AssetHierarchyLevels/bulk
   */
  async bulkDelete(request: BulkDeleteAssetHierarchyLevelsRequest): Promise<GuidBulkOperationResult> {
    const response = await ApiService.delete<GuidBulkOperationResult>('/AssetHierarchyLevels/bulk', { data: request });
    return response.data;
  },
};
